import type {
  MeasurementDefinitionDetail,
  SessionSummary,
} from '@/src/dal/session-dal'
import type { DimensionValue } from 'react-native'
import { Pressable, Text, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { QuerySuspenseBoundary } from '@/src/components/ui/QuerySuspenseBoundary'
import { countMeasurementsRequiringRecording } from '@/src/features/voice-assessment/components/VoiceAssessmentFlow.shared'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { sessionDefinitionQueryOptions } from '@/src/queries/session-queries'
import { colors } from '@/src/theme'
import { body3, body4, label1, label3 } from '@/src/theme/typography'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  VOICE_ASSESSMENT_CARD_OUTER_STYLE,
  VOICE_ASSESSMENT_FIGMA_GAP,
} from '../../constants/statistics.styles'
import {
  VoiceAssessmentExpandedDetailContentLoader,
  VoiceAssessmentExpandedDetailContentLoaderProps,
} from './VoiceAssessmentExpandedDetailContent'
import {
  VoiceAssessmentSessionAnalysisCardsLoader,
  VoiceAssessmentSessionAnalysisCardsLoaderProps,
} from './VoiceAssessmentSessionAnalysisCards'

interface VoiceAssessmentAccordionRowProps {
  readonly entry: SessionSummary
  readonly isLatest?: boolean
  readonly expanded: boolean
  readonly onToggle?: () => void
}

export interface VoiceAssessmentAccordionRowFallbackProps {
  readonly created: Date
  readonly isLatest: boolean
}

function toDateLabel(created: Date): string {
  return created.toISOString().slice(0, 10)
}

function useSkeletonAnimatedStyle() {
  const opacity = useDerivedValue(() =>
    withRepeat(
      withSequence(
        withTiming(0.7, { duration: 1000 }),
        withTiming(0.3, { duration: 1000 }),
      ),
      -1,
      true,
    ),
  )

  return useAnimatedStyle(() => ({
    opacity: opacity.value,
  }))
}

function SkeletonLine({
  testID,
  width,
  height,
}: {
  testID: string
  width: DimensionValue
  height: number
}) {
  const animatedStyle = useSkeletonAnimatedStyle()

  return (
    <Animated.View
      testID={testID}
      className="rounded"
      style={[
        {
          width,
          height,
          backgroundColor: colors.buttonSecondary,
        },
        animatedStyle,
      ]}
    />
  )
}

export function VoiceAssessmentAccordionRowSkeleton({
  created,
  isLatest,
}: VoiceAssessmentAccordionRowFallbackProps) {
  const { t } = useTranslation(['statistics'])

  return (
    <View
      testID="voice-assessment-accordion-row-skeleton"
      className="overflow-hidden bg-background-secondary"
      style={[
        VOICE_ASSESSMENT_CARD_OUTER_STYLE,
        isLatest
          ? {
              borderWidth: 1,
              borderColor: colors.coreSecondary,
            }
          : null,
        { marginBottom: VOICE_ASSESSMENT_FIGMA_GAP },
      ]}
    >
      <View className="flex-row items-start justify-between gap-3">
        <View className="flex-1">
          {isLatest ? (
            <View>
              <Text style={[label1, { color: colors.textHeader }]}>
                {t('statistics:voice_assessment_latest_title')}
              </Text>
              <Text style={[label3, { color: colors.textBody }]}>
                {toDateLabel(created)}
              </Text>
            </View>
          ) : (
            <View className="gap-2">
              <SkeletonLine
                testID="voice-assessment-accordion-row-skeleton-title"
                width="58%"
                height={18}
              />
              <SkeletonLine
                testID="voice-assessment-accordion-row-skeleton-subtitle"
                width="36%"
                height={14}
              />
            </View>
          )}
        </View>
        <SkeletonLine
          testID="voice-assessment-accordion-row-skeleton-chevron"
          width={22}
          height={22}
        />
      </View>
    </View>
  )
}

export function VoiceAssessmentAccordionRowErrorFallback({
  created,
  isLatest,
  onRetry,
}: VoiceAssessmentAccordionRowFallbackProps &
  Readonly<{
    onRetry: () => void
  }>) {
  const { t } = useTranslation(['statistics'])

  return (
    <View
      testID="voice-assessment-accordion-row-error"
      className="overflow-hidden bg-background-secondary"
      style={[
        VOICE_ASSESSMENT_CARD_OUTER_STYLE,
        isLatest
          ? {
              borderWidth: 1,
              borderColor: colors.coreSecondary,
            }
          : null,
        { marginBottom: VOICE_ASSESSMENT_FIGMA_GAP },
      ]}
    >
      <View className="gap-3">
        {isLatest ? (
          <Text style={[label1, { color: colors.textHeader }]}>
            {t('statistics:voice_assessment_latest_title')}
          </Text>
        ) : null}
        <View className="gap-1">
          <Text style={[label3, { color: colors.textBody }]}>
            {toDateLabel(created)}
          </Text>
          <Text style={[label1, { color: colors.textHeader }]}>
            {t('statistics:voice_assessment_history_row_error_title')}
          </Text>
          <Text style={[body3, { color: colors.textMuted }]}>
            {t('statistics:voice_assessment_history_row_error_message')}
          </Text>
        </View>
        <Pressable
          onPress={onRetry}
          className="self-start rounded-lg px-4 py-2.5 active:opacity-90"
          style={{ backgroundColor: colors.corePrimary }}
          accessibilityRole="button"
        >
          <Text style={[label1, { color: colors.ink }]}>
            {t('statistics:voice_assessment_retry_button')}
          </Text>
        </Pressable>
      </View>
    </View>
  )
}

function VoiceAssessmentExpandedDetailFallback() {
  return (
    <View testID="voice-assessment-expanded-detail-loading" className="gap-3">
      <SkeletonLine
        testID="voice-assessment-expanded-detail-loading-title"
        width="48%"
        height={18}
      />
      <View className="flex-row justify-between gap-3">
        <SkeletonLine
          testID="voice-assessment-expanded-detail-loading-answer-1"
          width="48%"
          height={58}
        />
        <SkeletonLine
          testID="voice-assessment-expanded-detail-loading-answer-2"
          width="48%"
          height={58}
        />
      </View>
    </View>
  )
}

function IncompleteBadge({ text }: { text: string }) {
  return (
    <View
      className="rounded-full px-2.5 py-0.5"
      style={{ backgroundColor: colors.fill }}
    >
      <Text style={[body4, { color: colors.textBody }]}>{text}</Text>
    </View>
  )
}

function VoiceAssessmentExpandedDetailErrorFallback({
  onRetry,
}: Readonly<{
  onRetry: () => void
}>) {
  const { t } = useTranslation(['statistics'])

  return (
    <View className="gap-3">
      <Text style={[body3, { color: colors.textMuted }]}>
        {t('statistics:voice_assessment_history_row_error_message')}
      </Text>
      <Pressable
        onPress={onRetry}
        className="self-start rounded-lg px-4 py-2.5 active:opacity-90"
        style={{ backgroundColor: colors.corePrimary }}
        accessibilityRole="button"
      >
        <Text style={[label1, { color: colors.ink }]}>
          {t('statistics:voice_assessment_retry_button')}
        </Text>
      </Pressable>
    </View>
  )
}

function VoiceAssessmentSessionAnalysisCardsBoundary(
  props: VoiceAssessmentSessionAnalysisCardsLoaderProps,
) {
  return (
    <QuerySuspenseBoundary
      loadingFallback={<VoiceAssessmentExpandedDetailFallback />}
      errorFallback={({ reset }) => (
        <VoiceAssessmentExpandedDetailErrorFallback onRetry={reset} />
      )}
    >
      <VoiceAssessmentSessionAnalysisCardsLoader {...props} />
    </QuerySuspenseBoundary>
  )
}

export function VoiceAssessmentExpandedDetailContentBoundary(
  props: VoiceAssessmentExpandedDetailContentLoaderProps,
) {
  return (
    <QuerySuspenseBoundary
      loadingFallback={<VoiceAssessmentExpandedDetailFallback />}
      errorFallback={({ reset }) => (
        <VoiceAssessmentExpandedDetailErrorFallback onRetry={reset} />
      )}
    >
      <VoiceAssessmentExpandedDetailContentLoader {...props} />
    </QuerySuspenseBoundary>
  )
}

function VoiceAssessmentAccordionExpandedBody({
  expanded,
  isCompletedExpandable,
  measurementDefinitions,
  sessionId,
}: Readonly<{
  expanded: boolean
  isCompletedExpandable: boolean
  measurementDefinitions: readonly MeasurementDefinitionDetail[]
  sessionId: number
}>) {
  if (!expanded) {
    return null
  }

  if (isCompletedExpandable) {
    return (
      <View className="w-full gap-3">
        <VoiceAssessmentSessionAnalysisCardsBoundary sessionId={sessionId} />
        <VoiceAssessmentExpandedDetailContentBoundary
          sessionId={sessionId}
          measurementDefinitions={measurementDefinitions}
          showTitles
        />
        {/* Personal reflection card */}
      </View>
    )
  }

  return null
}

export function VoiceAssessmentAccordionRow({
  entry,
  isLatest = false,
  expanded,
  onToggle,
}: VoiceAssessmentAccordionRowProps) {
  const { t } = useTranslation(['statistics'])
  const language = useContentfulLocale()

  const { data: sessionDefinition } = useSuspenseQuery(
    sessionDefinitionQueryOptions(entry.definition.id, language),
  )
  const exerciseSubtitle = t(
    'statistics:voice_assessment_history_exercise_count',
    {
      count: countMeasurementsRequiringRecording(
        sessionDefinition.measurementDefinitions,
      ),
    },
  )

  const isCompletedExpandable = entry.status === 'Completed'
  const showIncompleteBadge =
    entry.status === 'Active' || entry.status === 'Aborted'
  const showChevron = onToggle != null && isCompletedExpandable

  return (
    <View
      className="overflow-hidden bg-background-secondary"
      style={[
        VOICE_ASSESSMENT_CARD_OUTER_STYLE,
        isLatest
          ? {
              borderWidth: 1,
              borderColor: colors.coreSecondary,
            }
          : null,
        { marginBottom: VOICE_ASSESSMENT_FIGMA_GAP },
      ]}
    >
      <Pressable
        onPress={showChevron ? onToggle : undefined}
        disabled={!showChevron}
        className={showChevron ? 'active:opacity-95' : undefined}
        accessibilityRole={showChevron ? 'button' : undefined}
        accessibilityState={{ expanded }}
      >
        <View className="flex-row items-start justify-between gap-3">
          <View className="flex-1">
            {isLatest ? (
              <View className="flex-row items-center justify-between gap-2 mb-1">
                <Text style={[label1, { color: colors.textHeader }]}>
                  {t('statistics:voice_assessment_latest_title')}
                </Text>
                {showIncompleteBadge ? (
                  <IncompleteBadge
                    text={
                      entry.status === 'Aborted'
                        ? t('statistics:voice_assessment_aborted_title')
                        : t('statistics:voice_assessment_not_analyzed_title')
                    }
                  />
                ) : null}
              </View>
            ) : (
              <View className="gap-2">
                <View className="flex-row items-baseline justify-between gap-x-2 mb-0.5">
                  <View className="flex-row flex-wrap items-baseline gap-x-2 gap-y-1 flex-1">
                    <Text style={[label1, { color: colors.textHeader }]}>
                      {toDateLabel(entry.created)}
                    </Text>
                    <Text style={[body3, { color: colors.textMuted }]}>
                      {exerciseSubtitle}
                    </Text>
                  </View>
                  {showIncompleteBadge ? (
                    <IncompleteBadge
                      text={
                        entry.status === 'Aborted'
                          ? t('statistics:voice_assessment_aborted_title')
                          : t('statistics:voice_assessment_not_analyzed_title')
                      }
                    />
                  ) : null}
                </View>
              </View>
            )}
            {isLatest ? (
              <Text style={[label3, { color: colors.textBody }]}>
                {toDateLabel(entry.created)}
              </Text>
            ) : null}
          </View>
          {showChevron ? (
            <Icon
              name={
                expanded
                  ? symbolName('chevron.up', 'expand_less')
                  : symbolName('chevron.down', 'expand_more')
              }
              size={22}
              color="muted"
            />
          ) : null}
        </View>
      </Pressable>
      <VoiceAssessmentAccordionExpandedBody
        expanded={expanded}
        isCompletedExpandable={isCompletedExpandable}
        measurementDefinitions={sessionDefinition.measurementDefinitions}
        sessionId={entry.id}
      />
    </View>
  )
}
